/**
 * errorKeys oficiais do backend billing (Cards 18-22 + 22.7).
 * Fonte: resposta à validação de contrato do Card 23.
 *
 * Estrutura de erro retornada:
 * {
 *   "error": "snake_case",   // ignorar (só pra log do backend)
 *   "message": "...",         // USAR — já vem localizada (Accept-Language respeitado)
 *   "errorKey": "Pascal_Case_With_Underscore",  // CHAVEAR POR AQUI
 *   ...campos extras por errorKey (resource, current, max, planTier, upgradeTo, feature)
 * }
 */
export type BillingErrorKey =
  // Upgrade
  | 'Upgrade_Cannot_Be_Done_During_Trial'
  | 'Upgrade_Not_Available_For_Status'
  | 'Upgrade_Requires_Higher_Plan'
  | 'Upgrade_Requires_Card_Token'
  | 'Upgrade_Gateway_Unavailable_Try_Again'
  // Downgrade
  | 'Downgrade_Requires_Lower_Plan'
  | 'Downgrade_Blocked_By_Usage'
  // Cancel
  | 'Cancel_Not_Available_For_Status'
  | 'Cancel_Gateway_Unavailable_Try_Again'
  | 'Cancellation_Reason_Required'
  | 'Cancellation_Details_Required_When_Other'
  | 'Cancellation_Details_Max_Length_Exceeded'
  // Reactivate
  | 'Reactivate_Only_After_Cancel'
  | 'Reactivate_Period_Expired'
  | 'Reactivate_Still_In_Period'
  | 'Reactivate_Payment_Method_Required'
  | 'Reactivate_Requires_Card_Token'
  // Shared
  | 'Plan_Change_To_Same_Plan'
  | 'Subscription_Not_Found_For_Company'
  | 'Plan_Not_Found'
  // Feature / Limit (Card 20)
  | 'Plan_Limit_Exceeded_MaxWorkflows'
  | 'Plan_Limit_Exceeded_MaxAdmins'
  | 'Plan_Limit_Exceeded_MaxAutomations'
  | 'Plan_Limit_Exceeded_MaxFormsPublic'
  | 'Plan_Limit_Exceeded_MaxWhatsAppConversationsPerMonth'
  | 'Feature_Not_Available_AiParserEnabled'
  // Fallback pra qualquer errorKey não catalogada.
  | 'Unknown';

const KNOWN_ERROR_KEYS: readonly BillingErrorKey[] = [
  'Upgrade_Cannot_Be_Done_During_Trial',
  'Upgrade_Not_Available_For_Status',
  'Upgrade_Requires_Higher_Plan',
  'Upgrade_Requires_Card_Token',
  'Upgrade_Gateway_Unavailable_Try_Again',
  'Downgrade_Requires_Lower_Plan',
  'Downgrade_Blocked_By_Usage',
  'Cancel_Not_Available_For_Status',
  'Cancel_Gateway_Unavailable_Try_Again',
  'Cancellation_Reason_Required',
  'Cancellation_Details_Required_When_Other',
  'Cancellation_Details_Max_Length_Exceeded',
  'Reactivate_Only_After_Cancel',
  'Reactivate_Period_Expired',
  'Reactivate_Still_In_Period',
  'Reactivate_Payment_Method_Required',
  'Reactivate_Requires_Card_Token',
  'Plan_Change_To_Same_Plan',
  'Subscription_Not_Found_For_Company',
  'Plan_Not_Found',
  'Plan_Limit_Exceeded_MaxWorkflows',
  'Plan_Limit_Exceeded_MaxAdmins',
  'Plan_Limit_Exceeded_MaxAutomations',
  'Plan_Limit_Exceeded_MaxFormsPublic',
  'Plan_Limit_Exceeded_MaxWhatsAppConversationsPerMonth',
  'Feature_Not_Available_AiParserEnabled',
] as const;

export interface BillingUsageViolation {
  resource: string;
  current: number;
  limit: number;
}

export interface BillingHttpError {
  status: number;
  /** errorKey oficial (Pascal_Case_With_Underscore). 'Unknown' quando não catalogada. */
  errorKey: BillingErrorKey;
  /** Mensagem já localizada pelo backend (Accept-Language). Exibir direto. */
  message: string;
  /** Dados extras do 402 (limit/feature). Usados pra UX rica no modal de bloqueio. */
  resource?: string;
  current?: number;
  max?: number;
  feature?: string;
  planTier?: string;
  upgradeTo?: string;
  /** Preenchida só em Downgrade_Blocked_By_Usage. */
  violations: BillingUsageViolation[];
}

export function resolveBillingErrorKey(raw: string | null | undefined): BillingErrorKey {
  const candidate = (raw ?? '').trim();
  if (!candidate) return 'Unknown';
  return (KNOWN_ERROR_KEYS as readonly string[]).includes(candidate)
    ? (candidate as BillingErrorKey)
    : 'Unknown';
}

export function isUpgradeBlockedByTrial(errorKey: BillingErrorKey): boolean {
  return errorKey === 'Upgrade_Cannot_Be_Done_During_Trial';
}

export function isGatewayError(errorKey: BillingErrorKey): boolean {
  return (
    errorKey === 'Upgrade_Gateway_Unavailable_Try_Again' ||
    errorKey === 'Cancel_Gateway_Unavailable_Try_Again'
  );
}

export function isRequiresCardToken(errorKey: BillingErrorKey): boolean {
  return (
    errorKey === 'Upgrade_Requires_Card_Token' ||
    errorKey === 'Reactivate_Requires_Card_Token'
  );
}

export function isPlanLimitExceeded(errorKey: BillingErrorKey): boolean {
  return errorKey.startsWith('Plan_Limit_Exceeded_');
}

export function isFeatureUnavailable(errorKey: BillingErrorKey): boolean {
  return errorKey.startsWith('Feature_Not_Available_');
}
